/*
 * Public tokenized AP page (Phase D). Business-office contact opens this via a
 * secure link to view the quote + vendor package and upload the Purchase Order.
 * No login required — access is scoped by the token.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { Loader2, Upload, ShieldCheck, FileText } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function APPurchaseOrderPage() {
  const { token } = useParams();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [poNumber, setPoNumber] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/ap/${token}`);
      setBundle(r.data);
      setName(r.data.quote?.ap_contact_name || "");
      setEmail(r.data.quote?.ap_contact_email || "");
    } catch { toast.error("This link is invalid or expired."); } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a PO file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("po_number", poNumber);
      fd.append("submitted_by_name", name); fd.append("submitted_by_email", email);
      await axios.post(`${API}/api/ap/${token}/purchase-order`, fd);
      toast.success("Purchase Order submitted. Thank you!");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed."); } finally { setUploading(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6b7280]"><Loader2 className="h-5 w-5 animate-spin mr-2" />Loading…</div>;
  if (!bundle) return <div className="min-h-screen flex items-center justify-center text-[#6b7280]">Invalid or expired link.</div>;

  const { quote, vendor_package: vp, purchase_order: po } = bundle;

  return (
    <div className="min-h-screen bg-[#f9fafb]" data-testid="ap-po-page">
      <header className="bg-white border-b border-[#e5e7eb]">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 flex items-center h-10"><img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="h-12 w-auto" /></div>
      </header>
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-10">
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#007a9e] font-semibold">Accounts Payable</span>
        <h1 className="mt-1 text-2xl font-bold text-[#111827]">Purchase Order for {quote.school_name}</h1>

        <div className="mt-5 rounded-xl border border-[#e5e7eb] bg-white p-5">
          <div className="flex items-center justify-between">
            <div className="text-[14px] text-[#374151]">Quote {quote.quote_number}</div>
            <div className="text-xl font-bold text-[#111827]" data-testid="ap-quote-total">${Number(quote.annual_total).toLocaleString()}</div>
          </div>
          <div className="mt-1 text-[12px] text-[#6b7280]">MySchoolRx — Annual License</div>
        </div>

        <div className="mt-5 rounded-xl border border-[#e5e7eb] bg-white p-5" data-testid="ap-vendor-package">
          <div className="text-[12px] font-semibold uppercase tracking-wide text-[#6b7280] mb-2">Vendor Package</div>
          <div className="text-[13px] text-[#374151] space-y-1">
            <div><span className="font-semibold">Vendor:</span> {vp.vendor_legal_name}</div>
            <div><span className="font-semibold">Tax ID:</span> {vp.vendor_tax_id}</div>
            <div><span className="font-semibold">Payment terms:</span> {vp.payment_terms}</div>
            <div><span className="font-semibold">Remittance:</span> {vp.remittance_email}</div>
          </div>
          <p className="mt-2 text-[12px] text-[#6b7280]">{vp.instructions}</p>
        </div>

        {po && po.status ? (
          <div className="mt-5 rounded-xl border border-[#a7f3d0] bg-[#ecfdf5] p-5 text-center" data-testid="ap-po-submitted">
            <ShieldCheck className="h-8 w-8 text-[#059669] mx-auto" />
            <p className="mt-2 text-[14px] font-semibold text-[#065f46]">PO {po.po_number || "(no number)"} received — status: {po.status.replace("_", " ")}.</p>
            {po.invoice_number && <p className="mt-1 text-[13px] text-[#065f46]">Invoice: {po.invoice_number}</p>}
          </div>
        ) : (
          <div className="mt-5 rounded-xl border border-[#e5e7eb] bg-white p-5 space-y-3" data-testid="ap-upload-panel">
            <div className="text-[12px] font-semibold uppercase tracking-wide text-[#6b7280]"><FileText className="h-4 w-4 inline mr-1" />Upload signed Purchase Order</div>
            <Input placeholder="PO number (optional)" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} className="h-10" data-testid="ap-po-number" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} className="h-10" data-testid="ap-name" />
              <Input placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-10" data-testid="ap-email" />
            </div>
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.docx" className="block w-full text-[13px]" data-testid="ap-po-file" />
            <Button disabled={uploading} onClick={upload} className="bg-[#007a9e] hover:bg-[#00607d] text-white disabled:opacity-50" data-testid="ap-po-upload-btn">
              {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading…</> : <><Upload className="h-4 w-4 mr-2" />Submit Purchase Order</>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
